/**
 * TableToolbar — Floating gear menu that appears when hovering over a table.
 *
 * Provides add/remove row/column actions and header row toggle.
 * Positioned at the top-right corner of the hovered table.
 * Uses ProseMirror commands from prosemirror-tables via the editor's internal API.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { BlockNoteEditor } from '@blocknote/core';

interface TableToolbarProps {
  readonly editor: BlockNoteEditor<any, any, any> | null;
  readonly containerRef: React.RefObject<HTMLDivElement | null>;
}

interface ToolbarPosition {
  readonly top: number;
  readonly right: number;
  readonly visible: boolean;
}

interface MenuAction {
  readonly label: string;
  readonly icon: string;
  readonly onClick: () => void;
}

export function TableToolbar({ editor, containerRef }: TableToolbarProps): React.ReactElement | null {
  const [position, setPosition] = useState<ToolbarPosition>({ top: 0, right: 0, visible: false });
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const hoveredTableRef = useRef<HTMLElement | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track which table element is being hovered
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const tableWrapper = target.closest('[data-content-type="table"]');

      if (tableWrapper && tableWrapper instanceof HTMLElement) {
        // Clear any pending hide
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }

        hoveredTableRef.current = tableWrapper;
        const containerRect = container.getBoundingClientRect();
        const tableRect = tableWrapper.getBoundingClientRect();

        setPosition({
          top: tableRect.top - containerRect.top + container.scrollTop,
          right: containerRect.right - tableRect.right + 4,
          visible: true,
        });
      }
    };

    const handleMouseOut = (e: MouseEvent) => {
      const relatedTarget = e.relatedTarget as HTMLElement | null;

      // Don't hide if moving to the menu itself
      if (relatedTarget && menuRef.current?.contains(relatedTarget)) return;

      // Don't hide if still within a table
      if (relatedTarget?.closest('[data-content-type="table"]')) return;

      // Delay hide to allow moving to the gear menu
      hideTimeoutRef.current = setTimeout(() => {
        setPosition((prev) => ({ ...prev, visible: false }));
        setMenuOpen(false);
        hoveredTableRef.current = null;
      }, 200);
    };

    container.addEventListener('mouseover', handleMouseOver);
    container.addEventListener('mouseout', handleMouseOut);

    return () => {
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mouseout', handleMouseOut);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, [containerRef]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  // Keep toolbar visible when hovering the menu itself
  const handleMenuMouseEnter = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handleMenuMouseLeave = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setPosition((prev) => ({ ...prev, visible: false }));
      setMenuOpen(false);
      hoveredTableRef.current = null;
    }, 200);
  }, []);

  // --- Table manipulation actions ---

  const getTableBlock = useCallback(() => {
    if (!editor || !hoveredTableRef.current) return null;
    const blockId = hoveredTableRef.current.closest('[data-id]')?.getAttribute('data-id');
    if (!blockId) return null;
    return editor.getBlock(blockId);
  }, [editor]);

  const addRowBelow = useCallback(() => {
    const block = getTableBlock();
    if (!block || !editor) return;
    const content = (block as any).content;
    if (content?.type !== 'tableContent') return;
    const colCount = content.rows[0]?.cells.length ?? 3;
    const newRow = { cells: Array.from({ length: colCount }, () => []) };
    const updatedRows = [...content.rows, newRow];
    editor.updateBlock(block, { content: { type: 'tableContent', rows: updatedRows } } as any);
    setMenuOpen(false);
  }, [editor, getTableBlock]);

  const addColumnRight = useCallback(() => {
    const block = getTableBlock();
    if (!block || !editor) return;
    const content = (block as any).content;
    if (content?.type !== 'tableContent') return;
    const updatedRows = content.rows.map((row: any) => ({
      cells: [...row.cells, []],
    }));
    editor.updateBlock(block, { content: { type: 'tableContent', rows: updatedRows } } as any);
    setMenuOpen(false);
  }, [editor, getTableBlock]);

  const removeLastRow = useCallback(() => {
    const block = getTableBlock();
    if (!block || !editor) return;
    const content = (block as any).content;
    if (content?.type !== 'tableContent' || content.rows.length <= 1) return;
    const updatedRows = content.rows.slice(0, -1);
    editor.updateBlock(block, { content: { type: 'tableContent', rows: updatedRows } } as any);
    setMenuOpen(false);
  }, [editor, getTableBlock]);

  const removeLastColumn = useCallback(() => {
    const block = getTableBlock();
    if (!block || !editor) return;
    const content = (block as any).content;
    if (content?.type !== 'tableContent') return;
    if (content.rows[0]?.cells.length <= 1) return;
    const updatedRows = content.rows.map((row: any) => ({
      cells: row.cells.slice(0, -1),
    }));
    editor.updateBlock(block, { content: { type: 'tableContent', rows: updatedRows } } as any);
    setMenuOpen(false);
  }, [editor, getTableBlock]);

  const deleteTable = useCallback(() => {
    const block = getTableBlock();
    if (!block || !editor) return;
    editor.removeBlocks([block]);
    setPosition((prev) => ({ ...prev, visible: false }));
    setMenuOpen(false);
  }, [editor, getTableBlock]);

  if (!position.visible) return null;

  const actions: MenuAction[] = [
    { label: 'Add row below', icon: '➕ ↓', onClick: addRowBelow },
    { label: 'Add column right', icon: '➕ →', onClick: addColumnRight },
    { label: 'Remove last row', icon: '➖ ↓', onClick: removeLastRow },
    { label: 'Remove last column', icon: '➖ →', onClick: removeLastColumn },
    { label: 'Delete table', icon: '🗑️', onClick: deleteTable },
  ];

  return (
    <div
      ref={menuRef}
      className="absolute z-20"
      style={{ top: `${position.top}px`, right: `${position.right}px` }}
      onMouseEnter={handleMenuMouseEnter}
      onMouseLeave={handleMenuMouseLeave}
    >
      {/* Gear button */}
      <button
        type="button"
        onClick={() => setMenuOpen(!menuOpen)}
        className="w-6 h-6 flex items-center justify-center rounded bg-muted/80 hover:bg-muted border border-border shadow-sm transition-colors"
        aria-label="Table options"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        title="Table options"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="w-3.5 h-3.5 text-foreground"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M8.34 1.804A1 1 0 019.32 1h1.36a1 1 0 01.98.804l.295 1.473c.497.191.964.44 1.39.74l1.38-.55a1 1 0 011.12.37l.68 1.18a1 1 0 01-.16 1.17l-1.08.92c.04.33.06.665.06 1.003s-.02.672-.06 1.002l1.08.92a1 1 0 01.16 1.17l-.68 1.18a1 1 0 01-1.12.37l-1.38-.55c-.426.3-.893.549-1.39.74l-.295 1.473a1 1 0 01-.98.804H9.32a1 1 0 01-.98-.804l-.295-1.473a5.961 5.961 0 01-1.39-.74l-1.38.55a1 1 0 01-1.12-.37l-.68-1.18a1 1 0 01.16-1.17l1.08-.92A5.99 5.99 0 014.67 10c0-.338.02-.672.06-1.002l-1.08-.92a1 1 0 01-.16-1.17l.68-1.18a1 1 0 011.12-.37l1.38.55c.426-.3.893-.549 1.39-.74L8.34 1.804zM10 13a3 3 0 100-6 3 3 0 000 6z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Dropdown menu */}
      {menuOpen && (
        <div
          className="absolute top-full right-0 mt-1 min-w-[170px] rounded-md border bg-popover shadow-md py-1 z-50"
          role="menu"
        >
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              role="menuitem"
              className="w-full text-left px-3 py-1.5 text-sm text-foreground hover:bg-accent transition-colors flex items-center gap-2"
              onClick={action.onClick}
            >
              <span className="w-5 text-center text-xs">{action.icon}</span>
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
